import { Link } from 'react-router-dom';

/**
 * Page-level "what's worth your attention" strip.
 *
 * Every page in the dashboard can render a row of context-aware
 * action cards above its main content.  These are derived from the
 * page's local state (a project's open ticket count, a tenant's
 * pending invites, a row's stale-since timestamp) so the user
 * doesn't have to scan the page to find the next move.
 *
 * Cards collapse silently when the underlying signal is zero — a
 * page with nothing to do renders nothing here.  That keeps the
 * pattern noise-free; if you see a card, it's because there's
 * actually something to act on.
 *
 * @param {Object} props
 * @param {Array<{id, title, body?, cta, to, tone?, icon?, disabled?}>} props.actions
 *        Each action becomes a card. Tone drives the accent colour:
 *        - 'urgent' (red), 'warn' (orange), 'primary' (brand),
 *          'normal' (sky / default).
 * @param {string} [props.title] Optional section heading shown above
 *        the cards. Default = "What needs you".
 */
export function ContextActions({ actions = [], title = 'What needs you' }) {
  const visible = (actions || []).filter(Boolean);
  if (visible.length === 0) return null;
  return (
    <section className="ctx-actions">
      <div className="ctx-actions__title">{title}</div>
      <div className="ctx-actions__list">
        {visible.map((a) => (
          <ContextActionCard key={a.id} action={a} />
        ))}
      </div>
    </section>
  );
}

function ContextActionCard({ action: a }) {
  const tone = a.tone || 'normal';
  const inner = (
    <>
      {a.icon && <span className="ctx-actions__icon" aria-hidden="true">{a.icon}</span>}
      <div className="ctx-actions__body">
        <div className="ctx-actions__t">{a.title}</div>
        {a.body && <div className="ctx-actions__desc">{a.body}</div>}
      </div>
      <span className="ctx-actions__cta">{a.cta} →</span>
    </>
  );
  if (a.onClick && !a.to) {
    return (
      <button
        type="button"
        className={`ctx-actions__card ctx-actions__card--${tone}`}
        onClick={a.onClick}
        disabled={a.disabled}
      >
        {inner}
      </button>
    );
  }
  return (
    <Link
      to={a.to || '#'}
      className={`ctx-actions__card ctx-actions__card--${tone}`}
      aria-disabled={a.disabled || undefined}
    >
      {inner}
    </Link>
  );
}
